import { useRef, useState } from 'react';

import { AutocompleteInputChangeReason, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

import DevicesService from '~features/devices/services/devicesService';
import { OffsetPagedResults } from '~interfaces';
import Device from '~interfaces/device';
import i18n from '~lib/i18n';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import AsyncAutocomplete, { AsyncAutocompleteProps } from './asyncAutocomplete';

const service = getService(DevicesService);

interface DevicesAutocompleteProps extends Omit<
	AsyncAutocompleteProps<Device>,
	'options' | 'label'
> {
	label?: string;
	placeholder?: string;
	count?: number;
	deviceModelName?: string;
	/**
	 * Filter on organisation
	 */
	organisationId?: string | undefined;
}

const DevicesAutocomplete = ({
	label,
	placeholder = i18n.t('search', { ns: 'actions' }),
	count = 10,
	organisationId,
	deviceModelName,
	...asyncAutocompleteProps
}: DevicesAutocompleteProps) => {
	const { t } = useTranslation();

	const [searchQuery, setSearchQuery] = useState('');
	const [open, setOpen] = useState(false);

	const totalRef = useRef<number>(undefined);

	const getKey = (_: number, previousData: OffsetPagedResults<Device[]>) => {
		if (!open || (previousData && !previousData.hasMore)) {
			// reached the end
			return null;
		}

		return [
			'devices',
			{
				page: (previousData?.page ?? 0) + 1,
				pageSize: 10,
				organisationId: organisationId,
				searchQuery: searchQuery,
				deviceModelName: deviceModelName,
			},
		] as const;
	};

	const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
		getKey,
		([_, args]) => {
			return service.getDevices({
				page: args.page?.toString() ?? '1',
				pageSize: args.pageSize?.toString() ?? '10',
				searchQuery: args.searchQuery,
				deviceModelName: args.deviceModelName,
				organisationId: args.organisationId,
			});
		},
		{
			keepPreviousData: true,
			revalidateFirstPage: false,
			onSuccess: (data) => (totalRef.current = data[0]?.total),
		}
	);

	/**
	 * Just set the size of the pages
	 */
	const handleOverflow = () => {
		// This check prevents the set size from triggering, which in turn
		// forces the list to scroll up.
		if (totalRef.current != null && (size - 1) * count <= totalRef.current) {
			setSize(size + 1);
		}
	};

	const handleInputChange = (
		e: React.SyntheticEvent,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		setSearchQuery(value);

		asyncAutocompleteProps.onInputChange?.(e, value, reason);
	};

	return (
		<Stack sx={{ gap: Dimens.spacingXs }}>
			{label && <Typography variant='labelSm'>{label}</Typography>}
			<AsyncAutocomplete
				{...asyncAutocompleteProps}
				loading={isLoading || isValidating}
				placeholder={placeholder}
				noOptionsText={t('noResults', { ns: 'system' })}
				onOpen={() => setOpen(true)}
				options={data?.flatMap((el) => el.results) ?? []}
				onInputChange={handleInputChange}
				onOverflow={handleOverflow}
			/>
		</Stack>
	);
};

export default DevicesAutocomplete;
